import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationPriority } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { useHandler } from "@redotech/react-util/hook";
import {
  RedoBadge,
  type RedoBadgeColor,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@redotech/redo-web/components/ui/select";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { useUpdateConversationPriority } from "../../../hooks/use-conversation-mutations";

interface EditablePriorityCellProps {
  conversationId: ConversationId;
  priority: ConversationPriority | null;
}

function getPriorityConfig(priority: ConversationPriority): {
  label: string;
  color: RedoBadgeColor;
} {
  switch (priority) {
    case "CRITICAL":
      return { label: "Critical", color: "error" };
    case "HIGH":
      return { label: "High", color: "orange" };
    case "MEDIUM":
      return { label: "Medium", color: "warning" };
    case "LOW":
      return { label: "Low", color: "gray" };
    default:
      return { label: priority, color: "gray" };
  }
}

const PRIORITY_OPTIONS: Array<{
  value: ConversationPriority | "NONE";
  label: string;
  color: RedoBadgeColor;
}> = [
  { value: "NONE", label: "None", color: "gray" },
  { value: "LOW", label: "Low", color: "gray" },
  { value: "MEDIUM", label: "Medium", color: "warning" },
  { value: "HIGH", label: "High", color: "orange" },
  { value: "CRITICAL", label: "Critical", color: "error" },
];

export const EditablePriorityCell = memo(function EditablePriorityCell({
  conversationId,
  priority,
}: EditablePriorityCellProps) {
  const updatePriority = useUpdateConversationPriority();

  const currentValue = priority ?? "NONE";

  const handleValueChange = useHandler((value: string) => {
    const newPriority: ConversationPriority | null =
      value === "NONE" ? null : (value as ConversationPriority);

    updatePriority(conversationId, newPriority);
  });

  if (!priority) {
    return (
      <Select onValueChange={handleValueChange} value={currentValue}>
        <SelectTrigger size="sm">
          <SelectValue>
            <Text fontSize="xs" textColor="tertiary">
              -
            </Text>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {PRIORITY_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <RedoBadge color={option.color} size="sm" text={option.label} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const config = getPriorityConfig(priority);

  return (
    <Select onValueChange={handleValueChange} value={currentValue}>
      <SelectTrigger size="sm">
        <SelectValue>
          <RedoBadge color={config.color} size="sm" text={config.label} />
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {PRIORITY_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <RedoBadge color={option.color} size="sm" text={option.label} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
});
